import React, { useState } from 'react';
import { 
  Users, Package, Layers, Activity, AlertTriangle, Scale, CheckCircle2, Clock, 
  Settings, Search, ArrowLeftRight, Plus, Trash2, Camera, Database, Server, 
  Wifi, CreditCard, ArrowUpDown, ChevronRight, ChevronLeft, Check, RefreshCw,
  Phone, Monitor, Copy, Info, AlertCircle, FileText, ChevronDown, Lock
} from 'lucide-react';
import { toast } from 'sonner';

// ============================================================================
// MOCK DATA FOR INDUSTRIAL ERPS (INDIAN METAL SECTOR CONTEXT)
// ============================================================================
const MOCK_PARTIES = [
  { id: 'PT-01', name: 'Jindal Stainless Ltd', state: 'Haryana', type: 'Supplier', active: true, balance: 1450280 },
  { id: 'PT-02', name: 'Essar Steel Processing', state: 'Gujarat', type: 'Job-Worker', active: true, balance: -824000 },
  { id: 'PT-03', name: 'Aman Metal Finishers', state: 'Delhi NCR', type: 'Manufacturer', active: true, balance: 350000 },
  { id: 'PT-04', name: 'Bharat Heavy Fabrication', state: 'Maharashtra', type: 'Supplier', active: true, balance: 120500 },
  { id: 'PT-05', name: 'Apex Coil Cutters', state: 'Punjab', type: 'Job-Worker', active: false, balance: 0 },
  { id: 'PT-06', name: 'Rajkot Circle Co.', state: 'Gujarat', type: 'Manufacturer', active: true, balance: -540200 },
  { id: 'PT-07', name: 'Hindalco Indus', state: 'Odisha', type: 'Supplier', active: true, balance: 4290000 }
];

const MOCK_COILS = [
  { id: 'CO-2026-9081', party: 'Jindal Stainless Ltd', date: '2026-05-28', weight: 8940, type: 'SS-304', status: 'GREEN', lifecycle: 'OPEN' },
  { id: 'CO-2026-9082', party: 'Jindal Stainless Ltd', date: '2026-05-28', weight: 9120, type: 'SS-304', status: 'AMBER', lifecycle: 'OPEN' },
  { id: 'CO-2026-8924', party: 'Essar Steel Processing', date: '2026-05-27', weight: 11450, type: 'SS-316', status: 'GREEN', lifecycle: 'ISSUED' },
  { id: 'CO-2026-8801', party: 'Aman Metal Finishers', date: '2026-05-25', weight: 7850, type: 'HR-Coil', status: 'RED', lifecycle: 'OPEN' },
  { id: 'CO-2026-8799', party: 'Bharat Heavy Fabrication', date: '2026-05-24', weight: 6420, type: 'SS-430', status: 'GREEN', lifecycle: 'CLOSED' },
  { id: 'CO-2026-8722', party: 'Rajkot Circle Co.', date: '2026-05-23', weight: 10250, type: 'SS-304', status: 'GREY', lifecycle: 'OPEN' }
];

const MOCK_LEDGER = [
  { id: 'TXN-9011', date: '2026-05-28', party: 'Jindal Stainless Ltd', ref: 'INV-44102', description: 'Coil Processing Job rate charges - 3 Coils', debit: 125000, credit: 0, balance: 1450280 },
  { id: 'TXN-8974', date: '2026-05-26', party: 'Jindal Stainless Ltd', ref: 'PAY-88122', description: 'Bank Transfer - HDFC Corporate', debit: 0, credit: 500000, balance: 1325280 },
  { id: 'TXN-8942', date: '2026-05-25', party: 'Essar Steel Processing', ref: 'INV-44081', description: 'Circle cutting recovery scrap credit deduction', debit: 0, credit: 154000, balance: -824000 },
  { id: 'TXN-8902', date: '2026-05-24', party: 'Aman Metal Finishers', ref: 'PAY-87993', description: 'Advance payment received - RTGS', debit: 0, credit: 100000, balance: 350000 },
  { id: 'TXN-8841', date: '2026-05-22', party: 'Bharat Heavy Fabrication', ref: 'INV-43990', description: 'Circle cutting Job - HR Coils 8.0mm thickness', debit: 84500, credit: 0, balance: 120500 }
];

const MOCK_PROGRAMS = [
  { id: 'PRG-402', coilId: 'CO-2026-8924', date: '2026-05-27', circles: [{ size: '450mm', count: 120 }, { size: '320mm', count: 85 }], recovery: 78.4, rate: 18.50, operator: 'M. Singh' },
  { id: 'PRG-401', coilId: 'CO-2026-8799', date: '2026-05-24', circles: [{ size: '600mm', count: 42 }, { size: '200mm', count: 110 }], recovery: 81.2, rate: 16.20, operator: 'Devendra K.' }
];

export default function DesignSystemShowcase() {
  const [activeTab, setActiveTab] = useState('tokens'); // tokens, mobile, desktop
  const [selectedMobileScreen, setSelectedMobileScreen] = useState('dashboard'); // login, dashboard, coil-list, coil-detail, weighment, program, party-list
  const [selectedDesktopScreen, setSelectedDesktopScreen] = useState('dashboard'); // dashboard, coil-list, ledger, admin
  const [swipedCoilId, setSwipedCoilId] = useState(null); // Simulated horizontal swipe on mobile
  
  // Custom Keyboard state for mobile weighment form
  const [grossInput, setGrossInput] = useState('8940');
  const [netInput, setNetInput] = useState('');
  const [activeInputField, setActiveInputField] = useState('gross'); // gross, net
  const [isPhotoUploaded, setIsPhotoUploaded] = useState(false);

  // Sorting state for desktop table
  const [desktopSortField, setDesktopSortField] = useState('id');
  const [desktopSortAsc, setDesktopSortAsc] = useState(false);

  // Filter state for desktop table
  const [desktopFilterParty, setDesktopFilterParty] = useState('ALL');
  const [desktopFilterStatus, setDesktopFilterStatus] = useState('ALL');

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label}: ${text}`);
  };

  const handleKeypadPress = (val) => {
    if (activeInputField === 'gross') {
      if (val === 'DEL') {
        setGrossInput(prev => prev.slice(0, -1));
      } else {
        setGrossInput(prev => prev + val);
      }
    } else {
      if (val === 'DEL') {
        setNetInput(prev => prev.slice(0, -1));
      } else {
        setNetInput(prev => prev + val);
      }
    }
  };

  // Status LED helper
  const renderLed = (status) => {
    const colors = {
      GREEN: 'text-[#00E676] bg-[#00E676]',
      AMBER: 'text-[#FFB300] bg-[#FFB300]',
      RED: 'text-[#FF1744] bg-[#FF1744]',
      GREY: 'text-[#757575] bg-[#757575]'
    };
    return (
      <span className={`led-indicator ${colors[status] || colors.GREY}`} />
    );
  };

  return (
    <div className="min-h-screen bg-[#141414] text-[#E0E0E0] font-sans antialiased flex flex-col selection:bg-[#FF6B00] selection:text-black">
      
      {/* INDUSTRIAL TOP CONTROL HEADER */}
      <header className="border-b border-[#333] bg-[#1E1E1E] p-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-3 w-3 bg-[#FF6B00] animate-pulse rounded-sm" />
              <h1 className="font-mono text-lg font-bold uppercase tracking-wider text-white">
                METAL-CORE ERP SPEC : : DESIGN SYSTEM
              </h1>
            </div>
            <p className="text-xs font-mono text-[#999] mt-1">
              B2B SAAS CONSOLE // STAINLESS STEEL & RAW METAL JOB-WORK // INDIA MARKET
            </p>
          </div>

          {/* MAIN VIEWPORT NAVIGATION */}
          <div className="flex border border-[#333] p-1 bg-[#141414]">
            <button 
              onClick={() => setActiveTab('tokens')}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors duration-150 ${activeTab === 'tokens' ? 'bg-[#FF6B00] text-black font-semibold' : 'text-[#888] hover:text-white'}`}
            >
              <Database className="h-3.5 w-3.5" />
              1. Tokens & Components
            </button>
            <button 
              onClick={() => setActiveTab('mobile')}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors duration-150 ${activeTab === 'mobile' ? 'bg-[#FF6B00] text-black font-semibold' : 'text-[#888] hover:text-white'}`}
            >
              <Phone className="h-3.5 w-3.5" />
              2. Mobile PWA Simulator
            </button>
            <button 
              onClick={() => setActiveTab('desktop')}
              className={`flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors duration-150 ${activeTab === 'desktop' ? 'bg-[#FF6B00] text-black font-semibold' : 'text-[#888] hover:text-white'}`}
            >
              <Monitor className="h-3.5 w-3.5" />
              3. Desktop Web Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* WORKSPACE AREA */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6">
        
        {/* ====================================================================
            TAB 1: DESIGN TOKENS & SYSTEM BASE COMPONENTS
            ==================================================================== */}
        {activeTab === 'tokens' && (
          <div className="space-y-8 animate-fadeIn">
            
            {/* INTRO */}
            <div className="bg-[#1E1E1E] border border-[#333] p-4 font-mono text-xs text-[#888] leading-relaxed">
              <span className="text-[#FF6B00] font-semibold">INFO:</span> This B2B design framework is engineered for harsh industrial floor setups.
              It prioritizes high-contrast visibility, immediate mechanical feedback, and massive tap targets for gloved fingers. Zero soft consumer details.
            </div>

            {/* COLOR GRID */}
            <div className="border border-[#333] bg-[#1E1E1E]">
              <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2.5 flex items-center justify-between">
                <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white">Color Token Values</h3>
                <span className="text-[10px] font-mono text-[#666]">CLICK HEX TO COPY</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-y divide-[#333] border-collapse">
                <div onClick={() => copyToClipboard('#141414', 'Dark BG')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#141414] border border-[#333] mb-2" />
                  <p className="font-mono text-xs font-semibold text-white">#141414</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">industrial-bg</p>
                </div>
                <div onClick={() => copyToClipboard('#1E1E1E', 'Surface S1')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#1E1E1E] border border-[#333] mb-2" />
                  <p className="font-mono text-xs font-semibold text-white">#1E1E1E</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">industrial-s1</p>
                </div>
                <div onClick={() => copyToClipboard('#2A2A2A', 'Surface S2')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#2A2A2A] border border-[#333] mb-2" />
                  <p className="font-mono text-xs font-semibold text-white">#2A2A2A</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">industrial-s2</p>
                </div>
                <div onClick={() => copyToClipboard('#FF6B00', 'Forge Amber')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#FF6B00] mb-2" />
                  <p className="font-mono text-xs font-semibold text-[#FF6B00]">#FF6B00</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">accent</p>
                </div>
                <div onClick={() => copyToClipboard('#333333', 'Border Standard')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#333333] mb-2" />
                  <p className="font-mono text-xs font-semibold text-white">#333333</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">industrial-border</p>
                </div>
                
                {/* Status LED Row */}
                <div onClick={() => copyToClipboard('#00E676', 'LED Green')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#00E676] mb-2 flex items-center justify-center font-mono text-[10px] text-black">ACTIVE</div>
                  <p className="font-mono text-xs font-semibold text-[#00E676]">#00E676</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">led-green</p>
                </div>
                <div onClick={() => copyToClipboard('#FFB300', 'LED Amber')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#FFB300] mb-2 flex items-center justify-center font-mono text-[10px] text-black">PENDING</div>
                  <p className="font-mono text-xs font-semibold text-[#FFB300]">#FFB300</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">led-amber</p>
                </div>
                <div onClick={() => copyToClipboard('#FF1744', 'LED Red')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#FF1744] mb-2 flex items-center justify-center font-mono text-[10px] text-black">CRITICAL</div>
                  <p className="font-mono text-xs font-semibold text-[#FF1744]">#FF1744</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">led-red</p>
                </div>
                <div onClick={() => copyToClipboard('#757575', 'LED Grey')} className="p-4 cursor-pointer hover:bg-[#252525] transition-colors">
                  <div className="h-10 w-full bg-[#757575] mb-2 flex items-center justify-center font-mono text-[10px] text-black">INACTIVE</div>
                  <p className="font-mono text-xs font-semibold text-white">#757575</p>
                  <p className="text-[10px] text-[#666] font-mono uppercase">led-grey</p>
                </div>
                <div className="p-4 bg-[#141414] flex flex-col justify-center">
                  <p className="font-mono text-[10px] text-[#FF6B00] uppercase font-bold">Forge Amber Glow</p>
                  <p className="text-[10px] text-[#888] font-sans">Simulates radiant molten steel under extreme processing temps.</p>
                </div>
              </div>
            </div>

            {/* INTERACTIVE UI COMPONENT SANDBOX */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* BUTTONS PANEL */}
              <div className="border border-[#333] bg-[#1E1E1E]">
                <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2 flex items-center justify-between">
                  <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white">Mechanical Buttons</h3>
                  <span className="text-[9px] font-mono text-[#888]">SHARP OR MAX 2PX RADIUS</span>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <span className="block font-mono text-[10px] text-[#888] mb-2 uppercase">Primary CTA (Forge Amber solid)</span>
                    <button className="w-full bg-[#FF6B00] text-black font-mono font-bold uppercase text-xs tracking-wider py-3 px-4 rounded-[2px] hover:bg-[#E05E00] active:translate-y-[1px] transition-all duration-75 border-b-2 border-black/30">
                      EXECUTE WEIGHMENT
                    </button>
                  </div>
                  <div>
                    <span className="block font-mono text-[10px] text-[#888] mb-2 uppercase">Secondary Action (Subtle Slate Borderless Flat)</span>
                    <button className="w-full bg-[#2A2A2A] text-white font-mono uppercase text-xs tracking-wider py-3 px-4 rounded-[2px] border border-[#444] hover:bg-[#333] active:translate-y-[1px] transition-all">
                      CANCEL SETTLEMENT
                    </button>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <span className="block font-mono text-[10px] text-[#888] mb-2 uppercase">Danger Button</span>
                      <button className="w-full bg-[#FF1744] text-white font-mono uppercase text-xs tracking-wider py-3 px-4 rounded-[2px] hover:bg-[#D50000] active:translate-y-[1px] transition-all font-semibold">
                        VOID COIL RECORD
                      </button>
                    </div>
                    <div className="flex-1">
                      <span className="block font-mono text-[10px] text-[#888] mb-2 uppercase">Ghost (Accented Border on Hover)</span>
                      <button className="w-full bg-transparent text-[#888] hover:text-[#FF6B00] font-mono uppercase text-xs tracking-wider py-3 px-4 rounded-[2px] border border-[#333] hover:border-[#FF6B00] active:translate-y-[1px] transition-all">
                        REFRESH SYSTEM
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* INDUSTRIAL FORM PANEL */}
              <div className="border border-[#333] bg-[#1E1E1E]">
                <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2 flex items-center justify-between">
                  <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white">Monospace Form Inputs</h3>
                  <span className="text-[9px] font-mono text-[#888]">48PX MIN TOUCH TARGET</span>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block font-mono text-[10px] text-[#999] uppercase mb-1 font-semibold">COIL ID / MONOSPACE CODE</label>
                    <input 
                      type="text" 
                      defaultValue="SS-304-4482" 
                      className="w-full bg-[#141414] border border-[#333] text-white font-mono text-sm p-3 rounded-[2px] focus:outline-none focus:border-[#FF6B00] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] text-[#999] uppercase mb-1 font-semibold">CALIBRATED GROSS WEIGHT (KG)</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        defaultValue="8,940" 
                        className="w-full bg-[#141414] border border-[#333] text-[#FF6B00] font-mono text-base font-bold p-3 rounded-[2px] focus:outline-none focus:border-[#FF6B00] transition-colors pr-10"
                      />
                      <span className="absolute right-3 top-3.5 font-mono text-xs text-[#666]">KG</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* PROCESS LED CONTROL LIGHTS */}
            <div className="border border-[#333] bg-[#1E1E1E] p-4">
              <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white mb-4">Circular LED Process Lights (Not Chips)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-[#141414] border border-[#333] flex items-center gap-3">
                  {renderLed('GREEN')}
                  <div>
                    <p className="font-mono text-xs text-white uppercase font-bold">WEIGHED</p>
                    <p className="text-[10px] text-[#666] font-sans">Scale reading verified</p>
                  </div>
                </div>
                <div className="p-3 bg-[#141414] border border-[#333] flex items-center gap-3">
                  {renderLed('AMBER')}
                  <div>
                    <p className="font-mono text-xs text-white uppercase font-bold">PENDING</p>
                    <p className="text-[10px] text-[#666] font-sans">Awaiting physical weigh</p>
                  </div>
                </div>
                <div className="p-3 bg-[#141414] border border-[#333] flex items-center gap-3">
                  {renderLed('RED')}
                  <div>
                    <p className="font-mono text-xs text-white uppercase font-bold">OVERDUE / ALERT</p>
                    <p className="text-[10px] text-[#666] font-sans">Process run exceeds limits</p>
                  </div>
                </div>
                <div className="p-3 bg-[#141414] border border-[#333] flex items-center gap-3">
                  {renderLed('GREY')}
                  <div>
                    <p className="font-mono text-xs text-white uppercase font-bold">INACTIVE</p>
                    <p className="text-[10px] text-[#666] font-sans">Account not verified</p>
                  </div>
                </div>
              </div>
            </div>

            {/* DENSE METADATA TABLES SPECIFICATION */}
            <div className="border border-[#333] bg-[#1E1E1E]">
              <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2.5 flex items-center justify-between">
                <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-white">Dense Data Table Grid Spec</h3>
                <span className="text-[10px] font-mono text-[#FF6B00]">MONOSPACE ALIGNMENTS ON NUMBERS</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-[#333] bg-[#141414]">
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-[#888] uppercase tracking-wider">COIL CODE</th>
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-[#888] uppercase tracking-wider">PARTY NAME</th>
                      <th className="px-3 py-2 text-left font-mono text-[10px] text-[#888] uppercase tracking-wider">STATUS</th>
                      <th className="px-3 py-2 text-right font-mono text-[10px] text-[#888] uppercase tracking-wider">GROSS WEIGHT (KG)</th>
                      <th className="px-3 py-2 text-right font-mono text-[10px] text-[#888] uppercase tracking-wider">NET WEIGHT (KG)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222]">
                    <tr className="bg-[#1E1E1E] hover:bg-[#252525]">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-white">CO-2026-9081</td>
                      <td className="px-3 py-2 font-sans text-xs text-[#CCC]">Jindal Stainless Ltd</td>
                      <td className="px-3 py-2 flex items-center gap-2 text-xs">{renderLed('GREEN')} <span className="font-mono text-[10px] text-[#666]">VERIFIED</span></td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-white">8,940</td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-white">8,820</td>
                    </tr>
                    <tr className="bg-[#191919] hover:bg-[#252525]">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-white">CO-2026-9082</td>
                      <td className="px-3 py-2 font-sans text-xs text-[#CCC]">Jindal Stainless Ltd</td>
                      <td className="px-3 py-2 flex items-center gap-2 text-xs">{renderLed('AMBER')} <span className="font-mono text-[10px] text-[#666]">PENDING</span></td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-white">9,120</td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-[#666]">- - -</td>
                    </tr>
                    <tr className="bg-[#1E1E1E] hover:bg-[#252525]">
                      <td className="px-3 py-2 font-mono text-xs font-semibold text-white">CO-2026-8801</td>
                      <td className="px-3 py-2 font-sans text-xs text-[#CCC]">Aman Metal Finishers</td>
                      <td className="px-3 py-2 flex items-center gap-2 text-xs">{renderLed('RED')} <span className="font-mono text-[10px] text-[#666]">OVERLIMIT</span></td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-white">7,850</td>
                      <td className="px-3 py-2 font-mono text-xs text-right text-white">7,100</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {/* ====================================================================
            TAB 2: MOBILE OPERATOR APP FRAME (375PX PWA SIMULATOR)
            ==================================================================== */}
        {activeTab === 'mobile' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fadeIn">
            
            {/* MOBILE SCREEN SELECTOR */}
            <div className="lg:col-span-4 space-y-4">
              <div className="border border-[#333] bg-[#1E1E1E] p-4">
                <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-[#FF6B00] mb-3 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Mobile App Screens (PWA)
                </h3>
                <p className="text-xs text-[#888] leading-relaxed mb-4">
                  Simulates the 375px native standalone browser viewpoint used on the hot shop floor by workers.
                </p>
                <div className="flex flex-col gap-1">
                  {[
                    { id: 'login', label: '1. Operator Login' },
                    { id: 'dashboard', label: '2. Shop Dashboard' },
                    { id: 'coil-list', label: '3. Coil Inward List' },
                    { id: 'coil-detail', label: '4. Coil Card Detail' },
                    { id: 'weighment', label: '5. Weighment entry' },
                    { id: 'program', label: '6. Circle Program Sheet' },
                    { id: 'party-list', label: '7. Party Registry' }
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedMobileScreen(s.id)}
                      className={`text-left px-3 py-2.5 font-mono text-xs uppercase tracking-wider border transition-all duration-100 ${selectedMobileScreen === s.id ? 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00] font-bold' : 'border-[#222] bg-[#141414] hover:bg-[#252525] text-[#999]'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* SPECIFICATION CHECKS */}
              <div className="border border-[#333] bg-[#1E1E1E] p-4 font-mono text-[11px] text-[#888] space-y-2">
                <p className="text-white font-bold uppercase border-b border-[#333] pb-1">MOBILE DESIGN RULES:</p>
                <p>✓ Minimum 48px height touch buttons</p>
                <p>✓ High-contrast values for direct sunlight usage</p>
                <p>✓ Tactile custom numeric keypad for heavy gloves</p>
                <p>✓ Horizontal swipe actions on cards (void/archive)</p>
                <p>✓ Standalone window (PWA header space accounted)</p>
              </div>
            </div>

            {/* MOBILE INTERACTIVE DEVICE FRAME */}
            <div className="lg:col-span-8 flex justify-center">
              
              {/* PHYSICAL DEVICE OUTER FRAME */}
              <div className="relative mx-auto border-[10px] border-[#2A2A2A] rounded-[36px] bg-[#141414] shadow-2xl p-1 max-w-[395px] w-full min-h-[740px]">
                
                {/* PHYSICAL SPEAKER & CAMERA NOTCH */}
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 h-4 w-32 bg-[#2A2A2A] rounded-full z-30 flex items-center justify-center">
                  <div className="h-1 w-10 bg-[#111] rounded-full" />
                </div>

                {/* DEVICE SCREEN CANVAS (375px Width viewport simulated) */}
                <div className="bg-[#141414] text-[#E0E0E0] rounded-[26px] overflow-hidden min-h-[712px] flex flex-col relative border border-[#222] select-none">
                  
                  {/* SIMULATED PWA TOP STATUS BAR */}
                  <div className="bg-[#1E1E1E] h-10 px-6 pt-3 flex items-center justify-between text-[#888] font-mono text-[10px] border-b border-[#333] z-20">
                    <span>14:35</span>
                    <div className="flex items-center gap-2">
                      <Wifi className="h-3 w-3 text-[#FFB300]" /> {/* Spotty Wifi indicator */}
                      <span className="text-[#FFB300] font-bold text-[9px]">SPOTTY WIFI</span>
                      <div className="w-5 h-2.5 border border-[#666] p-0.5 rounded-sm flex items-center">
                        <div className="h-full w-[80%] bg-[#00E676]" />
                      </div>
                    </div>
                  </div>

                  {/* SCREEN SCROLLABLE BODY */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden pb-16 flex flex-col">
                    
                    {/* ==========================================================
                        SCREEN 1: LOGIN
                        ========================================================== */}
                    {selectedMobileScreen === 'login' && (
                      <div className="p-6 flex flex-col justify-between flex-1">
                        <div className="my-auto space-y-6">
                          <div className="text-center">
                            <span className="inline-block p-3 border border-[#333] bg-[#1E1E1E] mb-2 rounded-sm text-[#FF6B00]">
                              <Database className="h-8 w-8" />
                            </span>
                            <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white mt-1">METAL-CORE // ERP</h2>
                            <p className="text-[10px] font-mono text-[#666]">SHOP FLOOR OPERATOR TERMINAL</p>
                          </div>

                          <div className="bg-[#1E1E1E] border border-[#FFB300]/30 p-3 flex gap-3 text-left">
                            <AlertCircle className="h-4 w-4 text-[#FFB300] shrink-0 mt-0.5" />
                            <div>
                              <p className="font-mono text-[10px] text-[#FFB300] font-bold uppercase">Calibrated Scale Conn</p>
                              <p className="text-[9px] text-[#999] font-sans">Working offline. Weighments will sync locally once active.</p>
                            </div>
                          </div>

                          <div className="space-y-3 text-left">
                            <div>
                              <label className="block font-mono text-[10px] text-[#888] uppercase mb-1">OPERATOR ID</label>
                              <input 
                                type="text" 
                                placeholder="ENTER OPERATOR ID" 
                                className="w-full bg-[#1E1E1E] border border-[#333] p-3 text-sm font-mono text-[#FF6B00] rounded-sm focus:outline-none focus:border-[#FF6B00]" 
                              />
                            </div>
                            <div>
                              <label className="block font-mono text-[10px] text-[#888] uppercase mb-1">PASSWORD</label>
                              <input 
                                type="password" 
                                placeholder="••••••••" 
                                className="w-full bg-[#1E1E1E] border border-[#333] p-3 text-sm font-mono text-white rounded-sm focus:outline-none focus:border-[#FF6B00]" 
                              />
                            </div>
                          </div>

                          <button 
                            onClick={() => setSelectedMobileScreen('dashboard')}
                            className="w-full bg-[#FF6B00] text-black font-mono font-bold uppercase text-xs tracking-wider py-3.5 rounded-sm hover:bg-[#E05E00]"
                          >
                            AUTHENTICATE
                          </button>
                        </div>
                        <p className="text-[8px] font-mono text-[#444] text-center uppercase tracking-wider mt-4">METAL-WORKS B2B SAAS INDIA V2.81</p>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 2: SHOP DASHBOARD
                        ========================================================== */}
                    {selectedMobileScreen === 'dashboard' && (
                      <div className="p-4 space-y-4 text-left">
                        {/* Title block */}
                        <div>
                          <p className="text-[10px] font-mono text-[#FF6B00] uppercase">M. SINGH (OPERATOR)</p>
                          <h2 className="font-mono text-base font-bold text-white uppercase tracking-wide">FLOOR OVERVIEW</h2>
                        </div>

                        {/* Critical Pending Weighment alert light */}
                        <div 
                          onClick={() => setSelectedMobileScreen('weighment')}
                          className="bg-[#2A2A2A] border-l-4 border-[#FFB300] p-3 hover:bg-[#333] transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {renderLed('AMBER')}
                              <p className="font-mono text-xs font-semibold text-white uppercase">AWAITING WEIGHMENT</p>
                            </div>
                            <span className="font-mono text-[10px] bg-[#FFB300] text-black px-1.5 font-bold">01 COIL</span>
                          </div>
                          <p className="text-[9px] text-[#999] font-sans mt-1">Jindal Stainless coil inward batch awaiting floor confirmation.</p>
                        </div>

                        {/* Quick Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#1E1E1E] border border-[#333] p-3">
                            <span className="block font-mono text-[9px] text-[#666] uppercase">Open Coils</span>
                            <span className="font-mono text-xl font-bold text-white">04</span>
                          </div>
                          <div className="bg-[#1E1E1E] border border-[#333] p-3">
                            <span className="block font-mono text-[9px] text-[#666] uppercase">Issued Runs</span>
                            <span className="font-mono text-xl font-bold text-[#FF6B00]">02</span>
                          </div>
                        </div>

                        {/* Activity logs */}
                        <div className="border border-[#333] bg-[#1E1E1E]">
                          <div className="border-b border-[#333] bg-[#2A2A2A] px-3 py-1.5 flex items-center justify-between">
                            <h3 className="font-mono text-[9px] font-bold uppercase tracking-wider text-white">RECENT RUNS</h3>
                            <span className="text-[8px] font-mono text-[#666]">TODAY</span>
                          </div>
                          <div className="divide-y divide-[#222]">
                            <div className="p-2.5 flex justify-between items-start text-xs font-mono">
                              <div>
                                <p className="text-white font-semibold">CO-2026-8924</p>
                                <p className="text-[9px] text-[#666]">Issued to Machine-B // 450mm</p>
                              </div>
                              <span className="text-[9px] font-bold text-[#00E676] bg-[#00E676]/10 px-1">RUNNING</span>
                            </div>
                            <div className="p-2.5 flex justify-between items-start text-xs font-mono">
                              <div>
                                <p className="text-white font-semibold">CO-2026-8799</p>
                                <p className="text-[9px] text-[#666]">Completed // 81.2% Recovery</p>
                              </div>
                              <span className="text-[9px] font-bold text-[#888] bg-[#2A2A2A] px-1">CLOSED</span>
                            </div>
                          </div>
                        </div>

                        <button 
                          onClick={() => setSelectedMobileScreen('coil-list')}
                          className="w-full bg-[#2A2A2A] hover:bg-[#333] border border-[#444] font-mono text-xs py-3 text-center uppercase tracking-wider text-white"
                        >
                          VIEW INWARD COILS
                        </button>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 3: COIL INWARD LIST
                        ========================================================== */}
                    {selectedMobileScreen === 'coil-list' && (
                      <div className="p-4 space-y-3 text-left">
                        <div className="flex items-center justify-between">
                          <h2 className="font-mono text-xs font-bold uppercase tracking-wider text-[#FF6B00]">COIL REGISTER</h2>
                          <span className="font-mono text-[9px] text-[#666]">COILS: {MOCK_COILS.length}</span>
                        </div>

                        {/* Dense List */}
                        <div className="space-y-2">
                          {MOCK_COILS.map((coil) => (
                            <div 
                              key={coil.id}
                              onClick={() => setSelectedMobileScreen('coil-detail')}
                              className="bg-[#1E1E1E] border border-[#333] p-3 hover:border-[#FF6B00] transition-colors cursor-pointer flex items-center justify-between"
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs font-bold text-white">{coil.id}</span>
                                  <span className="text-[9px] font-mono text-[#888] bg-[#2A2A2A] px-1">{coil.type}</span>
                                </div>
                                <p className="font-sans text-[11px] text-[#999]">{coil.party}</p>
                                <p className="font-mono text-[9px] text-[#666]">{coil.date}</p>
                              </div>
                              
                              <div className="flex flex-col items-end gap-1.5">
                                <span className="font-mono text-xs font-semibold text-white">{coil.weight.toLocaleString()} kg</span>
                                <div className="flex items-center gap-1.5">
                                  {renderLed(coil.status)}
                                  <span className="text-[9px] font-mono text-[#888]">{coil.status === 'GREEN' ? 'WEIGHED' : 'PENDING'}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 4: COIL INWARD DETAIL (WITH HORIZONTAL SWIPE SIMULATION)
                        ========================================================== */}
                    {selectedMobileScreen === 'coil-detail' && (
                      <div className="p-4 space-y-4 text-left">
                        <button 
                          onClick={() => setSelectedMobileScreen('coil-list')}
                          className="flex items-center gap-1 text-[10px] font-mono text-[#FF6B00] uppercase"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" /> Back to Register
                        </button>

                        <div className="bg-[#1E1E1E] border border-[#333] p-4">
                          <div className="flex justify-between items-start border-b border-[#333] pb-3 mb-3">
                            <div>
                              <p className="font-mono text-[10px] text-[#FF6B00]">COIL ID SPECIFICATION</p>
                              <h2 className="font-mono text-lg font-bold text-white">CO-2026-9082</h2>
                              <p className="font-sans text-xs text-[#999] mt-0.5">Supplier: Jindal Stainless Ltd</p>
                            </div>
                            {renderLed('AMBER')}
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-xs font-mono border-b border-[#333] pb-3 mb-3">
                            <div>
                              <span className="block text-[9px] text-[#666] uppercase">Inward Date</span>
                              <span className="text-white">2026-05-28</span>
                            </div>
                            <div>
                              <span className="block text-[9px] text-[#666] uppercase">Metal Alloy Type</span>
                              <span className="text-white">SS-304 Grade</span>
                            </div>
                            <div>
                              <span className="block text-[9px] text-[#666] uppercase">Weight Status</span>
                              <span className="text-[#FFB300] font-bold">GROSS ONLY (AMBER)</span>
                            </div>
                            <div>
                              <span className="block text-[9px] text-[#666] uppercase">Declared Wt</span>
                              <span className="text-white">9,120 kg</span>
                            </div>
                          </div>

                          {/* Swipe Simulator Alert Block */}
                          <div className="bg-[#141414] border border-[#333] p-3 text-[10px] font-mono text-center">
                            <span className="text-[#FF6B00] font-bold uppercase">SWIPE RECORD FOR OPTIONS</span>
                            
                            {/* SIMULATED SWIPEABLE CARD CONTAINER */}
                            <div className="relative mt-2 h-14 bg-[#1E1E1E] overflow-hidden border border-[#2A2A2A]">
                              
                              {/* Hidden actions behind card */}
                              <div className="absolute inset-0 flex justify-end">
                                <button 
                                  onClick={() => {
                                    toast.error('Record marked as VOID');
                                    setSwipedCoilId(null);
                                  }}
                                  className="bg-[#FF1744] text-white px-4 font-mono text-[10px] font-bold"
                                >
                                  VOID
                                </button>
                                <button 
                                  onClick={() => {
                                    toast.success('Record archived successfully');
                                    setSwipedCoilId(null);
                                  }}
                                  className="bg-[#333] text-[#888] px-4 font-mono text-[10px]"
                                >
                                  ARCHIVE
                                </button>
                              </div>

                              {/* Forefront Card */}
                              <div 
                                onClick={() => setSwipedCoilId(swipedCoilId ? null : 'swipe')}
                                className={`absolute inset-0 bg-[#2A2A2A] p-2 flex items-center justify-between transition-transform duration-200 cursor-pointer ${swipedCoilId === 'swipe' ? '-translate-x-24' : 'translate-x-0'}`}
                              >
                                <div className="text-left font-mono">
                                  <p className="text-[9px] text-[#888]">SIMULATED SWIPING ACTION</p>
                                  <p className="text-[10px] text-white font-semibold">TAP TO SLIDE LEFT</p>
                                </div>
                                <ArrowLeftRight className="h-4 w-4 text-[#666]" />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button 
                            onClick={() => setSelectedMobileScreen('weighment')}
                            className="flex-1 bg-[#FF6B00] text-black font-mono font-bold text-xs uppercase py-3.5 text-center"
                          >
                            ENTER WEIGHMENT
                          </button>
                          <button 
                            onClick={() => setSelectedMobileScreen('program')}
                            className="flex-1 bg-[#2A2A2A] border border-[#333] font-mono text-xs uppercase py-3.5 text-center text-white"
                          >
                            VIEW JOB SHEET
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 5: WEIGHMENT ENTRY FORM
                        ========================================================== */}
                    {selectedMobileScreen === 'weighment' && (
                      <div className="p-4 space-y-4 text-left flex-1 flex flex-col justify-between">
                        <div>
                          <button 
                            onClick={() => setSelectedMobileScreen('coil-detail')}
                            className="flex items-center gap-1 text-[10px] font-mono text-[#FF6B00] uppercase mb-2"
                          >
                            <ChevronLeft className="h-3.5 w-3.5" /> Back to Detail
                          </button>

                          <h2 className="font-mono text-xs font-bold uppercase tracking-wider text-white border-b border-[#333] pb-1.5 mb-3">
                            WEIGHMENT SHEET: CO-2026-9082
                          </h2>

                          {/* Dual inputs */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div 
                              onClick={() => setActiveInputField('gross')}
                              className={`p-3 border cursor-pointer ${activeInputField === 'gross' ? 'border-[#FF6B00] bg-[#FF6B00]/5' : 'border-[#333] bg-[#1E1E1E]'}`}
                            >
                              <span className="block font-mono text-[9px] text-[#888] uppercase">GROSS WEIGHT (KG)</span>
                              <span className="font-mono text-base font-bold text-white">{grossInput || '_'}</span>
                            </div>
                            
                            <div 
                              onClick={() => setActiveInputField('net')}
                              className={`p-3 border cursor-pointer ${activeInputField === 'net' ? 'border-[#FF6B00] bg-[#FF6B00]/5' : 'border-[#333] bg-[#1E1E1E]'}`}
                            >
                              <span className="block font-mono text-[9px] text-[#888] uppercase">NET WEIGHT (KG)</span>
                              <span className="font-mono text-base font-bold text-white">{netInput || 'ENTER'}</span>
                            </div>
                          </div>

                          {/* Upload Photo Button */}
                          <div className="mb-4">
                            <span className="block font-mono text-[9px] text-[#888] mb-2 uppercase">UPLOAD SCALE CALIBRATION PHOTO</span>
                            <button 
                              onClick={() => {
                                setIsPhotoUploaded(true);
                                toast.success('Photo successfully uploaded from camera module');
                              }}
                              className={`w-full py-4 border border-dashed rounded-sm font-mono text-xs uppercase flex flex-col items-center justify-center gap-2 ${isPhotoUploaded ? 'border-[#00E676] text-[#00E676] bg-[#00E676]/5' : 'border-[#444] text-[#888] hover:border-[#FF6B00] hover:text-[#FF6B00]'}`}
                            >
                              {isPhotoUploaded ? (
                                <>
                                  <CheckCircle2 className="h-5 w-5" />
                                  PHOTO_LOADED_VERIFIED.JPG
                                </>
                              ) : (
                                <>
                                  <Camera className="h-5 w-5" />
                                  ACTIVATE CAMERA SCANNER
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* BIG NUMERIC TOUCH KEYPAD DESIGN FOR GLOVES */}
                        <div className="space-y-1">
                          <span className="block font-mono text-[9px] text-[#666] text-center uppercase mb-1">SHOP KEYPAD</span>
                          <div className="grid grid-cols-3 gap-1 bg-[#1E1E1E] p-1 border border-[#333]">
                            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(num => (
                              <button 
                                key={num}
                                onClick={() => handleKeypadPress(num)}
                                className="bg-[#141414] hover:bg-[#2A2A2A] font-mono text-sm py-3 text-white border border-[#222]"
                              >
                                {num}
                              </button>
                            ))}
                            <button 
                              onClick={() => handleKeypadPress('DEL')}
                              className="bg-[#FF1744]/15 text-[#FF1744] hover:bg-[#FF1744]/30 font-mono text-xs py-3 border border-[#222]"
                            >
                              DELETE
                            </button>
                            <button 
                              onClick={() => handleKeypadPress('0')}
                              className="bg-[#141414] hover:bg-[#2A2A2A] font-mono text-sm py-3 text-white border border-[#222]"
                            >
                              0
                            </button>
                            <button 
                              onClick={() => {
                                if (!grossInput || !netInput) {
                                  toast.error('Both weights required');
                                  return;
                                }
                                toast.success('Weighment submitted successfully');
                                setSelectedMobileScreen('dashboard');
                              }}
                              className="bg-[#FF6B00] text-black font-mono font-bold text-xs py-3 border border-transparent"
                            >
                              CONFIRM
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 6: PRODUCTION PROGRAM DETAIL
                        ========================================================== */}
                    {selectedMobileScreen === 'program' && (
                      <div className="p-4 space-y-4 text-left">
                        <button 
                          onClick={() => setSelectedMobileScreen('coil-detail')}
                          className="flex items-center gap-1 text-[10px] font-mono text-[#FF6B00] uppercase"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" /> Back to Coil
                        </button>

                        <div className="bg-[#1E1E1E] border border-[#333] p-4 font-mono text-xs space-y-3">
                          <div className="border-b border-[#333] pb-2">
                            <span className="text-[9px] text-[#666] block">JOB PROGRAM KEY</span>
                            <span className="text-white font-bold">PRG-2026-402</span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-[11px] border-b border-[#333] pb-2">
                            <div>
                              <span className="text-[9px] text-[#666] block">COIL REFER</span>
                              <span className="text-white">CO-2026-8924</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-[#666] block">ALLOY</span>
                              <span className="text-white">SS-316</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-[#666] block">JOB RATE (INR)</span>
                              <span className="text-[#FF6B00] font-bold">₹18.50/kg</span>
                            </div>
                            <div>
                              <span className="text-[9px] text-[#666] block">TARGET RECOVERY</span>
                              <span className="text-[#00E676] font-bold">78.4%</span>
                            </div>
                          </div>

                          {/* Circles to cut */}
                          <div>
                            <span className="text-[9px] text-[#666] block mb-1">CIRCLE CUTTING BLUEPRINT</span>
                            <div className="space-y-1">
                              <div className="bg-[#141414] p-2 flex justify-between text-[11px]">
                                <span className="text-white font-bold">450mm Diameter</span>
                                <span className="text-[#FF6B00]">120 Units</span>
                              </div>
                              <div className="bg-[#141414] p-2 flex justify-between text-[11px]">
                                <span className="text-white font-bold">320mm Diameter</span>
                                <span className="text-[#FF6B00]">85 Units</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#1E1E1E] border border-[#FF1744]/20 p-3 text-[10px] font-mono flex gap-2">
                          <AlertTriangle className="h-4 w-4 text-[#FF1744] shrink-0" />
                          <span className="text-[#999]">Safety gear check: Helmets and industrial thermal gloves must be active for cutting run.</span>
                        </div>
                      </div>
                    )}

                    {/* ==========================================================
                        SCREEN 7: PARTY LIST
                        ========================================================== */}
                    {selectedMobileScreen === 'party-list' && (
                      <div className="p-4 space-y-3 text-left">
                        <div className="flex justify-between items-center">
                          <h2 className="font-mono text-xs font-bold uppercase tracking-wider text-[#FF6B00]">REGISTERED CLIENTS</h2>
                          <span className="font-mono text-[9px] text-[#666]">TOTAL: {MOCK_PARTIES.length}</span>
                        </div>

                        <div className="space-y-2">
                          {MOCK_PARTIES.map(party => (
                            <div 
                              key={party.id}
                              className="bg-[#1E1E1E] border border-[#333] p-3 flex justify-between items-center"
                            >
                              <div>
                                <span className="font-mono text-[10px] text-[#FF6B00] block">{party.id}</span>
                                <p className="font-sans text-xs text-white font-bold">{party.name}</p>
                                <p className="text-[10px] text-[#666] font-mono">{party.state} // {party.type}</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-mono text-xs font-semibold ${party.balance < 0 ? 'text-[#FF1744]' : party.balance > 0 ? 'text-[#00E676]' : 'text-[#888]'}`}>
                                  {party.balance === 0 ? '₹0.00' : party.balance < 0 ? `₹${Math.abs(party.balance).toLocaleString()} Dr` : `₹${party.balance.toLocaleString()} Cr`}
                                </p>
                                <div className="flex justify-end gap-1.5 mt-1.5">
                                  {renderLed(party.active ? 'GREEN' : 'GREY')}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>

                  {/* BOTTOM MOBILE NAV BAR */}
                  <div className="absolute bottom-0 inset-x-0 h-14 bg-[#1E1E1E] border-t border-[#333] flex items-center justify-around z-20">
                    <button 
                      onClick={() => setSelectedMobileScreen('dashboard')}
                      className={`flex flex-col items-center gap-1 text-[#888] ${selectedMobileScreen === 'dashboard' ? 'text-[#FF6B00]' : ''}`}
                    >
                      <Activity className="h-4 w-4" />
                      <span className="text-[8px] font-mono uppercase">Floor</span>
                    </button>
                    <button 
                      onClick={() => setSelectedMobileScreen('coil-list')}
                      className={`flex flex-col items-center gap-1 text-[#888] ${selectedMobileScreen === 'coil-list' || selectedMobileScreen === 'coil-detail' ? 'text-[#FF6B00]' : ''}`}
                    >
                      <Package className="h-4 w-4" />
                      <span className="text-[8px] font-mono uppercase">Coils</span>
                    </button>
                    <button 
                      onClick={() => setSelectedMobileScreen('weighment')}
                      className={`flex flex-col items-center gap-1 text-[#888] ${selectedMobileScreen === 'weighment' ? 'text-[#FF6B00]' : ''}`}
                    >
                      <Scale className="h-4 w-4" />
                      <span className="text-[8px] font-mono uppercase">Scale</span>
                    </button>
                    <button 
                      onClick={() => setSelectedMobileScreen('party-list')}
                      className={`flex flex-col items-center gap-1 text-[#888] ${selectedMobileScreen === 'party-list' ? 'text-[#FF6B00]' : ''}`}
                    >
                      <Users className="h-4 w-4" />
                      <span className="text-[8px] font-mono uppercase">Parties</span>
                    </button>
                  </div>

                </div>

              </div>

            </div>
          </div>
        )}

        {/* ====================================================================
            TAB 3: DESKTOP WEB App FRAME (1280PX SIMULATOR)
            ==================================================================== */}
        {activeTab === 'desktop' && (
          <div className="space-y-6 animate-fadeIn">
            
            {/* SCREEN SELECTOR / BROWSER TABS */}
            <div className="flex flex-col md:flex-row md:items-center justify-between border border-[#333] bg-[#1E1E1E] p-2 gap-3">
              <div className="flex flex-wrap gap-1 bg-[#141414] p-1 border border-[#2A2A2A]">
                {[
                  { id: 'dashboard', label: '1. Manager Dashboard' },
                  { id: 'coil-list', label: '2. Coil Register & Filters' },
                  { id: 'ledger', label: '3. double-entry ledger' },
                  { id: 'admin', label: '4. SaaS Admin Panel' }
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedDesktopScreen(s.id)}
                    className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors duration-100 ${selectedDesktopScreen === s.id ? 'bg-[#FF6B00] text-black font-semibold' : 'text-[#888] hover:text-white'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              
              <div className="font-mono text-[10px] text-[#666] flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#00E676] inline-block animate-pulse" />
                <span>SERVER_NODE ONLINE // LATENCY 14MS</span>
              </div>
            </div>

            {/* SIMULATED WEB BROWSER FRAME */}
            <div className="border border-[#333] bg-[#1E1E1E] shadow-2xl flex flex-col min-h-[600px] text-left">
              
              {/* BROWSER CHROME HEADER */}
              <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF1744] inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FFB300] inline-block" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00E676] inline-block" />
                </div>
                <div className="bg-[#141414] border border-[#333] px-3 py-1 rounded-sm text-[10px] font-mono text-[#888] flex-1 max-w-sm flex items-center gap-2">
                  <Lock className="h-3 w-3 text-[#00E676]" />
                  <span>https://erp.metalworks.in/{selectedDesktopScreen}</span>
                </div>
              </div>

              {/* BROWSER WORKSPACE CANVAS */}
              <div className="flex-1 bg-[#141414] p-6 flex flex-col">
                
                {/* ==========================================================
                    SCREEN 1: DESKTOP DASHBOARD
                    ========================================================== */}
                {selectedDesktopScreen === 'dashboard' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-mono text-lg font-bold text-white uppercase tracking-wider">MANAGER CONTROL STATION</h2>
                      <p className="text-xs text-[#888] font-mono mt-0.5">Welcome, Admin Munim // Account Settlement Overview</p>
                    </div>

                    {/* Pending Weighment Banner */}
                    <div className="bg-[#2A2A2A] border border-[#FF1744] p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex gap-3 items-start">
                        <AlertTriangle className="h-5 w-5 text-[#FF1744] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-mono text-xs font-bold text-white uppercase">Calibrated Weighment Risk Detected</h4>
                          <p className="text-[11px] text-[#888] font-sans mt-0.5">
                            One active inward coil is currently using party declared weights instead of physical machine weighment. This presents audit risks.
                          </p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setSelectedDesktopScreen('coil-list')}
                        className="bg-[#FF1744] text-white font-mono text-[10px] uppercase font-bold py-2 px-3 border-b-2 border-black/30 shrink-0"
                      >
                        INVESTIGATE RISK
                      </button>
                    </div>

                    {/* Stats 4-Column Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { title: 'Total Inward Batches', value: '42 Coils', color: 'text-white' },
                        { title: 'At Risk (Supplier Weights)', value: '01 Coil', color: 'text-[#FF1744] font-bold' },
                        { title: 'Pending Settlement Jobs', value: '08 Jobs', color: 'text-[#FFB300] font-bold' },
                        { title: 'Total Outward Scrap Credit', value: '₹14.28 Lakhs', color: 'text-[#00E676] font-bold' }
                      ].map((card, idx) => (
                        <div key={idx} className="bg-[#1E1E1E] border border-[#333] p-4">
                          <span className="block font-mono text-[10px] text-[#666] uppercase">{card.title}</span>
                          <span className={`block font-mono text-lg mt-1 ${card.color}`}>{card.value}</span>
                        </div>
                      ))}
                    </div>

                    {/* Dense Inner Table */}
                    <div className="border border-[#333] bg-[#1E1E1E]">
                      <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2.5">
                        <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-white">LATEST INWARD COIL QUEUE</h4>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#333] bg-[#141414] text-[9px] font-mono text-[#888] uppercase">
                            <th className="p-3 text-left">BATCH ID</th>
                            <th className="p-3 text-left">SUPPLIER PARTY</th>
                            <th className="p-3 text-left">ALLOY</th>
                            <th className="p-3 text-right">WEIGHT</th>
                            <th className="p-3 text-left">STATUS</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#222] font-mono text-xs">
                          {MOCK_COILS.slice(0, 3).map(coil => (
                            <tr key={coil.id} className="hover:bg-[#252525]">
                              <td className="p-3 text-white font-bold">{coil.id}</td>
                              <td className="p-3 text-[#AAA] font-sans">{coil.party}</td>
                              <td className="p-3 text-[#666]">{coil.type}</td>
                              <td className="p-3 text-right text-white">{coil.weight.toLocaleString()} kg</td>
                              <td className="p-3 flex items-center gap-2">
                                {renderLed(coil.status)}
                                <span className="text-[10px] text-[#888]">{coil.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ==========================================================
                    SCREEN 2: COIL INWARD LIST WITH DENSE FILTERS
                    ========================================================== */}
                {selectedDesktopScreen === 'coil-list' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="font-mono text-lg font-bold text-white uppercase tracking-wider">COIL MANAGEMENT</h2>
                        <p className="text-xs text-[#888] font-mono mt-0.5">Filter, search, and sort raw coils inside the storage bays</p>
                      </div>
                      <button className="bg-[#FF6B00] text-black font-mono text-[10px] uppercase font-bold py-2.5 px-4 flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        RECORD NEW INWARD BATCH
                      </button>
                    </div>

                    {/* Filter bar */}
                    <div className="bg-[#1E1E1E] border border-[#333] p-3 flex flex-wrap gap-4 items-center">
                      <div className="flex items-center gap-2 border-r border-[#333] pr-4">
                        <Search className="h-4 w-4 text-[#666]" />
                        <input 
                          type="text" 
                          placeholder="Search Coil ID..." 
                          className="bg-transparent text-xs font-mono focus:outline-none text-white w-40" 
                        />
                      </div>
                      
                      {/* Party Filter dropdown */}
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#666] uppercase">PARTY:</span>
                        <select 
                          value={desktopFilterParty}
                          onChange={(e) => setDesktopFilterParty(e.target.value)}
                          className="bg-[#141414] border border-[#333] text-xs font-mono p-1 text-white focus:outline-none"
                        >
                          <option value="ALL">ALL PARTIES</option>
                          <option value="Jindal Stainless Ltd">JINDAL</option>
                          <option value="Essar Steel Processing">ESSAR</option>
                          <option value="Aman Metal Finishers">AMAN</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#666] uppercase">LED STATUS:</span>
                        <select 
                          value={desktopFilterStatus}
                          onChange={(e) => setDesktopFilterStatus(e.target.value)}
                          className="bg-[#141414] border border-[#333] text-xs font-mono p-1 text-white focus:outline-none"
                        >
                          <option value="ALL">ALL STATUSES</option>
                          <option value="GREEN">GREEN (WEIGHED)</option>
                          <option value="AMBER">AMBER (PENDING)</option>
                          <option value="RED">RED (OVERLIMIT)</option>
                        </select>
                      </div>
                    </div>

                    {/* Large Dense Data Table */}
                    <div className="border border-[#333] bg-[#1E1E1E]">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#333] bg-[#141414] text-[9px] font-mono text-[#888] uppercase">
                            <th className="p-3 text-left cursor-pointer hover:text-white" onClick={() => { setDesktopSortField('id'); setDesktopSortAsc(!desktopSortAsc); }}>
                              COIL CODE <ArrowUpDown className="h-3 w-3 inline ml-1 text-[#FF6B00]" />
                            </th>
                            <th className="p-3 text-left">SUPPLIER</th>
                            <th className="p-3 text-left">ALLOY</th>
                            <th className="p-3 text-right">GROSS WT</th>
                            <th className="p-3 text-left">DATE RECEIVED</th>
                            <th className="p-3 text-left">STATUS LED</th>
                            <th className="p-3 text-left">LIFECYCLE</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#222] font-mono text-xs">
                          {MOCK_COILS
                            .filter(c => desktopFilterParty === 'ALL' ? true : c.party === desktopFilterParty)
                            .filter(c => desktopFilterStatus === 'ALL' ? true : c.status === desktopFilterStatus)
                            .map(coil => (
                              <tr key={coil.id} className="hover:bg-[#252525] transition-colors">
                                <td className="p-3 text-white font-bold">{coil.id}</td>
                                <td className="p-3 font-sans text-[#AAA]">{coil.party}</td>
                                <td className="p-3 text-[#666]">{coil.type}</td>
                                <td className="p-3 text-right text-white font-semibold">{coil.weight.toLocaleString()} kg</td>
                                <td className="p-3 text-[#888]">{coil.date}</td>
                                <td className="p-3 flex items-center gap-2">
                                  {renderLed(coil.status)}
                                  <span className="text-[10px] text-[#666]">{coil.status === 'GREEN' ? 'WEIGHED' : 'AWAITING'}</span>
                                </td>
                                <td className="p-3">
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${coil.lifecycle === 'CLOSED' ? 'bg-[#222] text-[#666]' : coil.lifecycle === 'ISSUED' ? 'bg-orange-500/10 text-orange-500 border border-orange-500/20' : 'bg-green-500/10 text-green-500 border border-green-500/20'}`}>
                                    {coil.lifecycle}
                                  </span>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ==========================================================
                    SCREEN 3: PARTY LEDGER / ACCOUNT SUMMARY
                    ========================================================== */}
                {selectedDesktopScreen === 'ledger' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-mono text-lg font-bold text-white uppercase tracking-wider">DOUBLE-ENTRY LEDGER MATRIX</h2>
                      <p className="text-xs text-[#888] font-mono mt-0.5">Verified financial statements and outstanding party balances</p>
                    </div>

                    {/* Parties Overview summary grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-[#1E1E1E] border border-[#333] p-4 text-xs font-mono">
                        <span className="text-[#666] block">NET RECEIVABLE (TOTAL CR)</span>
                        <span className="text-lg text-[#00E676] font-bold mt-1 block">₹50,91,280</span>
                      </div>
                      <div className="bg-[#1E1E1E] border border-[#333] p-4 text-xs font-mono">
                        <span className="text-[#666] block">NET PAYABLE (TOTAL DR)</span>
                        <span className="text-lg text-[#FF1744] font-bold mt-1 block">₹13,64,200</span>
                      </div>
                      <div className="bg-[#1E1E1E] border border-[#333] p-4 text-xs font-mono">
                        <span className="text-[#666] block">LEDGER AUDIT REPORT</span>
                        <span className="text-xs text-[#FFB300] font-bold mt-1.5 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#00E676]" /> ALL ACCOUNTS BALANCED</span>
                      </div>
                    </div>

                    {/* Ledger Transactions table */}
                    <div className="border border-[#333] bg-[#1E1E1E]">
                      <div className="border-b border-[#333] bg-[#2A2A2A] px-4 py-2.5">
                        <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-white">LEDGER ENTRIES</h4>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#333] bg-[#141414] text-[9px] font-mono text-[#888] uppercase">
                            <th className="p-3 text-left">TXN ID</th>
                            <th className="p-3 text-left">DATE</th>
                            <th className="p-3 text-left">PARTY DETAILS</th>
                            <th className="p-3 text-left">REFERENCE</th>
                            <th className="p-3 text-right">DEBIT (DR)</th>
                            <th className="p-3 text-right">CREDIT (CR)</th>
                            <th className="p-3 text-right">RUNNING BALANCE</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#222] font-mono text-xs">
                          {MOCK_LEDGER.map(row => (
                            <tr key={row.id} className="hover:bg-[#252525]">
                              <td className="p-3 text-white font-bold">{row.id}</td>
                              <td className="p-3 text-[#888]">{row.date}</td>
                              <td className="p-3 text-white font-sans font-semibold">{row.party}</td>
                              <td className="p-3 text-[#666]">{row.ref}</td>
                              <td className="p-3 text-right text-[#FF1744]">{row.debit === 0 ? '-' : `₹${row.debit.toLocaleString()}`}</td>
                              <td className="p-3 text-right text-[#00E676]">{row.credit === 0 ? '-' : `₹${row.credit.toLocaleString()}`}</td>
                              <td className="p-3 text-right text-white font-bold">{`₹${Math.abs(row.balance).toLocaleString()} ${row.balance < 0 ? 'Dr' : 'Cr'}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ==========================================================
                    SCREEN 4: ADMIN PANEL
                    ========================================================== */}
                {selectedDesktopScreen === 'admin' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-mono text-lg font-bold text-white uppercase tracking-wider">B2B SAAS GLOBAL CONSOLE</h2>
                      <p className="text-xs text-[#888] font-mono mt-0.5">Software configurations, multi-tenant billing, and subscription licensing</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      
                      {/* Subscription Info */}
                      <div className="bg-[#1E1E1E] border border-[#333] p-4 space-y-4">
                        <h4 className="font-mono text-xs font-bold text-white uppercase border-b border-[#333] pb-2 text-[#FF6B00]">LICENSING SUBSCRIPTION</h4>
                        <div className="space-y-2.5 font-mono text-xs">
                          <div className="flex justify-between">
                            <span className="text-[#666]">Active Tier:</span>
                            <span className="text-white font-bold">PRO ENTERPRISE MULTI-SITE</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#666]">Renewal Date:</span>
                            <span className="text-white">2027-01-30</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#666]">Client Slots Used:</span>
                            <span className="text-[#00E676] font-bold">14 / 20 Sites</span>
                          </div>
                        </div>
                      </div>

                      {/* Active Tenants List */}
                      <div className="bg-[#1E1E1E] border border-[#333] p-4 space-y-3 md:col-span-2">
                        <h4 className="font-mono text-xs font-bold text-white uppercase border-b border-[#333] pb-2 text-[#FF6B00]">MULTI-TENANT SITES (INDIA CONTEXT)</h4>
                        <div className="divide-y divide-[#333] font-mono text-xs">
                          <div className="py-2 flex justify-between">
                            <span>1. Gujarat Metal Alloys (Ahmedabad Shop)</span>
                            <span className="text-[#00E676] font-bold uppercase">LICENSED</span>
                          </div>
                          <div className="py-2 flex justify-between">
                            <span>2. NCR Stainless Tubes (Faridabad Site)</span>
                            <span className="text-[#00E676] font-bold uppercase">LICENSED</span>
                          </div>
                          <div className="py-2 flex justify-between">
                            <span>3. Pune Precision Circle Cutters</span>
                            <span className="text-[#FFB300] font-bold uppercase">TRIAL RUN (12 DAYS REMAINING)</span>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                )}

              </div>

            </div>

          </div>
        )}

      </main>

      {/* FOOTER METADATA */}
      <footer className="border-t border-[#333] bg-[#1E1E1E] p-4 mt-8 font-mono text-[10px] text-[#666] text-center uppercase tracking-widest">
        METAL-CORE DESIGN SYSTEM // B2B ERP STAINLESS STEEL COIL ERP MODULE // DEVELOPED BY ANTIGRAVITY DEEPMIND
      </footer>

    </div>
  );
}
